import { create } from "zustand";
import { persist } from "zustand/middleware";

// Types
export interface UserInfo {
  id: number | null;
  name: string;
  email: string;
  phone: string;
  profile_types: { id: number; profile_type: number }[];
}

export interface AuthState {
  // Auth state
  isAuthenticated: boolean;
  user: {
    token: string | null;
    refresh: string | null;
  };
  userInfo: UserInfo;

  // Actions
  login: (token: string, refresh: string, userInfo: UserInfo) => void;
  logout: () => void;
  updateTokens: (token: string, refresh: string) => void;
  updateUserInfo: (userInfo: Partial<UserInfo>) => void;

  // Getters
  getToken: () => string | null;
  getRefreshToken: () => string | null;
}

const initialUserInfo: UserInfo = {
  id: null,
  name: "",
  email: "",
  phone: "",
  profile_types: [],
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      user: {
        token: null,
        refresh: null,
      },
      userInfo: initialUserInfo,

      // Actions
      login: (token: string, refresh: string, userInfo: UserInfo) => {
        set({
          isAuthenticated: true,
          user: { token, refresh },
          userInfo,
        });
      },

      logout: () => {
        set({
          isAuthenticated: false,
          user: { token: null, refresh: null },
          userInfo: initialUserInfo,
        });
      },

      updateTokens: (token: string, refresh: string) => {
        set(() => ({
          user: { token, refresh },
          isAuthenticated: !!token,
        }));
      },

      updateUserInfo: (newUserInfo: Partial<UserInfo>) => {
        set((state) => ({
          userInfo: { ...state.userInfo, ...newUserInfo },
        }));
      },

      // Getters
      getToken: () => get().user.token,
      getRefreshToken: () => get().user.refresh,
    }),
    {
      name: "connectifyz-auth",
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        userInfo: state.userInfo,
      }),
    }
  )
);

export default useAuthStore;
